import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Grid, Paper, Divider, Button, Tooltip, Chip
} from "@mui/material";
import {
  People as PeopleIcon, School as SchoolIcon,
  PersonOutline as TeacherIcon, AccountBalance as PaymentIcon,
  VolunteerActivism as VolunteerIcon, SupportAgent as ParaIcon,
  OpenInNew as DrillDownIcon
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { fetchData } from "../../BaseUrl";
import Toast from "../../DynamicComponent/Toast";
import AcademicCountdown from "../../DynamicComponent/AcademicCountdown";

const GRADES = [
  "MunMazhalai", "Mazhalai",
  "Nilai 1", "Nilai 2", "Nilai 3", "Nilai 4",
  "Nilai 5", "Nilai 6", "Nilai 7", "Nilai 8"
];

const GRADE_COLORS = [
  "#244C77", "#4A7BA8", "#2196f3", "#03a9f4",
  "#00bcd4", "#009688", "#4caf50", "#8bc34a",
  "#ff9800", "#f44336"
];


const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [stats, setStats] = useState({
    totalRegistrations: 0,
    activeStudents: 0,
    totalTeachers: 0,
    paraProfessional: 6
  });
  const [gradeData, setGradeData] = useState([]);
  const [activeGradeData, setActiveGradeData] = useState([]);
  const [staffData, setStaffData] = useState([]);
  const [nonTeachingRoles, setNonTeachingRoles] = useState([]);
  const [volunteerStats, setVolunteerStats] = useState({ total: 0, approved: 0, pending: 0, teaching: 0, nonTeaching: 0, paraProfessional: 0 });
  const [paymentSummary, setPaymentSummary] = useState({
    totalPayments: 0, pending: 0, refunds: 0, reimbursements: 0
  });
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  const today = new Date();

  const loadDashboard = useCallback(async () => {
    try {
      const result = await fetchData("admin/admission/applications?limit=1");
      if (result?.data) {
        const pagination = result.data.pagination || {};
        setStats(prev => ({ ...prev, totalRegistrations: pagination.total || 0 }));
      }
    } catch (err) { /* silent — table may be empty or not yet seeded */ }

    try {
      const studentsRes = await fetchData("admin/master/students?limit=1");
      if (studentsRes?.data) {
        setStats(prev => ({ ...prev, activeStudents: studentsRes.data.pagination?.total || 0 }));
      }
    } catch (err) { /* silent */ }

    try {
      const teachersRes = await fetchData("admin/master/teachers?limit=1");
      if (teachersRes?.data) {
        setStats(prev => ({
          ...prev,
          totalTeachers: teachersRes.data.pagination?.total || 0
        }));
      }
    } catch (err) { /* silent */ }

    try {
      const volRes = await fetchData("admin/volunteer-admin/stats");
      if (volRes?.data) {
        const d = volRes.data;
        setVolunteerStats(prev => ({
          ...prev,
          total:           d.total           || 0,
          approved:        d.approved        || 0,
          pending:         d.pending         || 0,
          teaching:        d.active          || 0,
          nonTeaching:     d.inactive        || 0,
          paraProfessional: d.waitlisted     || 0,
        }));
      }
    } catch (err) { /* silent */ }

    // Non-teaching staff by role
    try {
      const ntRes = await fetchData("admin/master/staff?limit=100&role=non-teaching");
      const list = ntRes?.data?.staff || ntRes?.data?.items || ntRes?.data || [];
      if (Array.isArray(list) && list.length > 0) {
        const roleMap = {};
        list.forEach(s => {
          const r = s.role || s.designation || "Other";
          roleMap[r] = (roleMap[r] || 0) + 1;
        });
        const NT_COLORS = ["#244C77","#4A7BA8","#2196f3","#4caf50","#ff9800","#f44336","#00bcd4"];
        setNonTeachingRoles(Object.entries(roleMap).map(([role, count], i) => ({
          role, count, color: NT_COLORS[i % NT_COLORS.length]
        })));
      } else {
        // Fallback representative data
        setNonTeachingRoles([
          { role: "Secretary",        count: 2, color: "#244C77" },
          { role: "Treasurer",        count: 1, color: "#4A7BA8" },
          { role: "Admin Assistant",  count: 3, color: "#2196f3" },
          { role: "Para Professional",count: 4, color: "#4caf50" },
          { role: "Security",         count: 1, color: "#ff9800" },
          { role: "Storage Coord.",   count: 1, color: "#f44336" },
        ]);
      }
    } catch (err) {
      setNonTeachingRoles([
        { role: "Secretary",        count: 2, color: "#244C77" },
        { role: "Treasurer",        count: 1, color: "#4A7BA8" },
        { role: "Admin Assistant",  count: 3, color: "#2196f3" },
        { role: "Para Professional",count: 4, color: "#4caf50" },
        { role: "Security",         count: 1, color: "#ff9800" },
        { role: "Storage Coord.",   count: 1, color: "#f44336" },
      ]);
    }

    try {
      const dashRes = await fetchData("admin/dashboard");
      if (dashRes?.data) {
        setDashboardData(dashRes.data);
        if (dashRes.data.paraProfessional !== undefined) {
          setStats(prev => ({ ...prev, paraProfessional: dashRes.data.paraProfessional }));
        }
        // Registration by grade chart
        if (dashRes.data.registrations?.byGrade) {
          const gradeMap = {};
          dashRes.data.registrations.byGrade.forEach(g => {
            gradeMap[g.gradeApplyingFor] = parseInt(g.count) || 0;
          });
          setGradeData(GRADES.map((g, i) => ({ grade: g, count: gradeMap[g] || 0, color: GRADE_COLORS[i] })));
        }
        // Active students by grade donut chart
        if (dashRes.data.students?.byGrade) {
          const activeMap = {};
          dashRes.data.students.byGrade.forEach(g => {
            activeMap[g.assignedGrade] = parseInt(g.count) || 0;
          });
          setActiveGradeData(GRADES.map((g, i) => ({ grade: g, count: activeMap[g] || 0, color: GRADE_COLORS[i] })));
        }
      }
    } catch (err) { /* silent */ }

    try {
      const payRes = await fetchData("admin/payment/dashboard");
      if (payRes?.data) {
        const d = payRes.data;
        setPaymentSummary({
          totalPayments:   d.totalCollected || 0,
          pending:         (d.reimbursements?.pendingAmount || 0) + (d.refunds?.pendingAmount || 0),
          refunds:         d.refunds?.pendingAmount || 0,
          reimbursements:  d.reimbursements?.pendingAmount || 0
        });
      }
    } catch (err) { /* silent */ }

    // Teaching staff by grade
    try {
      const teachersRes = await fetchData("admin/master/teachers?limit=100");
      if (teachersRes?.data) {
        const teacherList = teachersRes.data.teachers || teachersRes.data.items || teachersRes.data || [];
        const gradeMap = {};
        teacherList.forEach(t => {
          const grade = t.assignedGrade || t.grade || "Unassigned";
          gradeMap[grade] = (gradeMap[grade] || 0) + 1;
        });
        const gradeKeys = Object.keys(gradeMap);
        if (gradeKeys.length > 0) {
          setStaffData(gradeKeys.map((g, i) => ({
            grade: g,
            count: gradeMap[g],
            color: GRADE_COLORS[i % GRADE_COLORS.length]
          })));
        } else if (teacherList.length > 0) {
          // No grade assignment — show a single total bar
          setStaffData([{ grade: "Total", count: teacherList.length, color: GRADE_COLORS[0] }]);
        }
      }
    } catch (err) { /* silent */ }
  }, []);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  const maxGradeCount  = Math.max(...gradeData.map(g => g.count), 1);
  const maxStaffCount  = Math.max(...staffData.map(s => s.count), 1);
  const maxNTCount     = Math.max(...nonTeachingRoles.map(r => r.count), 1);
  const totalNonTeaching = nonTeachingRoles.reduce((s, r) => s + r.count, 0);
  const totalStudents  = activeGradeData.reduce((sum, g) => sum + g.count, 0);

  const kpiCards = [
    { label: "Total Registrations", value: stats.totalRegistrations, color: "#244C77", bg: "#EAF1F8", icon: <PeopleIcon /> },
    { label: "Active Students", value: stats.activeStudents, color: "#4caf50", bg: "#e8f5e9", icon: <SchoolIcon /> },
    { label: "Total Teachers", value: stats.totalTeachers, color: "#2196f3", bg: "#e3f2fd", icon: <TeacherIcon /> },
    { label: "Para-Professional", value: dashboardData?.paraProfessional || stats.paraProfessional || 6, color: "#ff9800", bg: "#fff3e0", icon: <ParaIcon /> }
  ];

  // Calendar generation
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1).getDay();
  const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
  const calendarCells = [];
  for (let i = 0; i < firstDay; i++) calendarCells.push(null);
  for (let d = 1; d <= daysInMonth; d++) calendarCells.push(d);

  // Donut chart for Active Students by Grade
  const DONUT_R = 60;
  const DONUT_CX = 80;
  const DONUT_CY = 80;
  const STROKE_WIDTH = 18;
  const circumference = 2 * Math.PI * DONUT_R;

  // Compute proper SVG donut using stroke-dasharray/offset
  let donutOffset = 0;
  const donutPaths = activeGradeData.map((g, i) => {
    const fraction = totalStudents > 0 ? g.count / totalStudents : 0;
    const dash = fraction * circumference;
    const currentOffset = circumference - donutOffset;
    donutOffset += dash;
    return { ...g, dash, offset: currentOffset };
  });

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="body2" color="text.secondary" mb={1}>Pages / Dashboard</Typography>
      <Typography variant="h5" fontWeight="bold" mb={1}>Welcome to Cummings!</Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Here is your school overview for today.
      </Typography>
      <AcademicCountdown />

      {/* KPI Cards */}
      <Grid container spacing={2} mb={3}>
        {kpiCards.map((kpi, idx) => (
          <Grid item xs={12} sm={6} md={3} key={idx}>
            <Paper sx={{ p: 2, borderRadius: 2, backgroundColor: kpi.bg, display: "flex", alignItems: "center", gap: 2 }}>
              <Box sx={{
                width: 48, height: 48, borderRadius: 2, backgroundColor: kpi.color + "20",
                display: "flex", alignItems: "center", justifyContent: "center", color: kpi.color
              }}>
                {kpi.icon}
              </Box>
              <Box>
                <Typography variant="h4" fontWeight="bold" color={kpi.color}>{kpi.value}</Typography>
                <Typography variant="body2" color="text.secondary">{kpi.label}</Typography>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        {/* Registration by Grade Chart */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold">Registrations by Grade</Typography>
              <Button size="small" endIcon={<DrillDownIcon sx={{ fontSize: 14 }} />}
                onClick={() => navigate("registrations")}
                sx={{ textTransform: "none", color: "#244C77", fontSize: 12 }}>
                View All
              </Button>
            </Box>
            <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1, height: 200 }}>
              {gradeData.map((g, i) => (
                <Tooltip key={i} title={`${g.grade}: ${g.count} registrations — click to filter`} arrow>
                  <Box
                    onClick={() => navigate(`registrations?grade=${encodeURIComponent(g.grade)}`)}
                    sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", cursor: "pointer",
                      "&:hover .bar": { opacity: 0.75 } }}>
                    <Typography variant="caption" fontWeight="bold" mb={0.5}>{g.count}</Typography>
                    <Box className="bar" sx={{
                      width: "100%", maxWidth: 40,
                      height: `${Math.max((g.count / maxGradeCount) * 160, g.count > 0 ? 6 : 0)}px`,
                      backgroundColor: g.color,
                      borderRadius: "4px 4px 0 0",
                      transition: "height 0.3s ease, opacity 0.2s"
                    }} />
                    <Typography variant="caption" sx={{ mt: 0.5, fontSize: 9, textAlign: "center" }}>
                      {g.grade.replace("Nilai ", "N")}
                    </Typography>
                  </Box>
                </Tooltip>
              ))}
            </Box>
          </Paper>
        </Grid>

        {/* Calendar Widget */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" mb={2}>
              {MONTHS[today.getMonth()]} {today.getFullYear()}
            </Typography>
            <Grid container spacing={0}>
              {DAYS.map(d => (
                <Grid item xs={12 / 7} key={d}>
                  <Typography variant="caption" align="center" display="block" fontWeight="bold" color="text.secondary">
                    {d}
                  </Typography>
                </Grid>
              ))}
              {calendarCells.map((day, i) => (
                <Grid item xs={12 / 7} key={i}>
                  <Box sx={{
                    textAlign: "center", py: 0.5, borderRadius: 1,
                    backgroundColor: day === today.getDate() ? "#244C77" : "transparent",
                    color: day === today.getDate() ? "#fff" : "text.primary"
                  }}>
                    <Typography variant="caption">{day || ""}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>


        {/* Active Students by Grade — Donut */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, borderRadius: 2, display: "flex", flexDirection: "column", alignItems: "center" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%", mb: 1 }}>
              <Typography variant="subtitle1" fontWeight="bold">Active Students by Grade</Typography>
              <Tooltip title="View admissions"><DrillDownIcon sx={{ fontSize: 16, color: "#244C77", cursor: "pointer" }}
                onClick={() => navigate("admissions")} /></Tooltip>
            </Box>
            {/* Donut SVG */}
            <Box sx={{ position: "relative", width: 160, height: 160, mb: 1 }}>
              <svg width="160" height="160" viewBox="0 0 160 160">
                {donutPaths.map((seg, i) => (
                  <circle
                    key={i}
                    cx={DONUT_CX}
                    cy={DONUT_CY}
                    r={DONUT_R}
                    fill="none"
                    stroke={seg.color}
                    strokeWidth={STROKE_WIDTH}
                    strokeDasharray={`${seg.dash} ${circumference - seg.dash}`}
                    strokeDashoffset={seg.offset}
                    style={{ transition: "stroke-dasharray 0.4s ease" }}
                  />
                ))}
                <text x={DONUT_CX} y={DONUT_CY - 6} textAnchor="middle" fontSize="18" fontWeight="bold" fill="#1a237e">
                  {totalStudents}
                </text>
                <text x={DONUT_CX} y={DONUT_CY + 12} textAnchor="middle" fontSize="10" fill="#757575">
                  Students
                </text>
              </svg>
            </Box>
            {/* Mini legend */}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, justifyContent: "center", mb: 1.5 }}>
              {activeGradeData.map((g, i) => (
                <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: g.color }} />
                  <Typography variant="caption" sx={{ fontSize: 9 }}>
                    {g.grade.replace("Nilai ", "N")}: {g.count}
                  </Typography>
                </Box>
              ))}
            </Box>
            <Button size="small" variant="outlined"
              onClick={() => navigate("admissions")}
              sx={{ textTransform: "none", borderColor: "#244C77", color: "#244C77", fontSize: 12 }}>
              Details
            </Button>
          </Paper>
        </Grid>

        {/* Teaching Staff Bar Chart */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold">Teaching Staff by Grade</Typography>
              <Tooltip title="View master"><DrillDownIcon sx={{ fontSize: 16, color: "#244C77", cursor: "pointer" }}
                onClick={() => navigate("master")} /></Tooltip>
            </Box>
            <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1, height: 160 }}>
              {staffData.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ m: "auto" }}>
                  No teacher data available
                </Typography>
              ) : staffData.map((s, i) => (
                <Tooltip key={i} title={`${s.grade}: ${s.count} teacher(s)`} arrow>
                  <Box onClick={() => navigate(`master?grade=${encodeURIComponent(s.grade)}`)}
                    sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", cursor: "pointer",
                      "&:hover .sbar": { opacity: 0.75 } }}>
                    <Typography variant="caption" fontWeight="bold" mb={0.5} sx={{ fontSize: 9 }}>{s.count}</Typography>
                    <Box className="sbar" sx={{
                      width: "100%", maxWidth: 32,
                      height: `${Math.max((s.count / maxStaffCount) * 120, s.count > 0 ? 6 : 0)}px`,
                      backgroundColor: s.color,
                      borderRadius: "4px 4px 0 0",
                      transition: "height 0.3s ease, opacity 0.2s"
                    }} />
                    <Typography variant="caption" sx={{ mt: 0.5, fontSize: 8, textAlign: "center" }}>
                      {s.grade.replace("MunMazhalai", "MM").replace("Mazhalai", "Maz").replace("Nilai ", "N")}
                    </Typography>
                  </Box>
                </Tooltip>
              ))}
            </Box>
          </Paper>
        </Grid>

        {/* Non-Teaching Staff by Role */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold">Non-Teaching Staff by Role</Typography>
              <Tooltip title="View master"><DrillDownIcon sx={{ fontSize: 16, color: "#244C77", cursor: "pointer" }}
                onClick={() => navigate("master")} /></Tooltip>
            </Box>
            {nonTeachingRoles.map((r, i) => {
              const pct = Math.round((r.count / maxNTCount) * 100);
              return (
                <Box key={i} sx={{ mb: 1.5 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.4 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={500}>{r.role}</Typography>
                    <Typography variant="caption" fontWeight={700} color={r.color}>{r.count}</Typography>
                  </Box>
                  <Box sx={{ height: 8, borderRadius: 4, backgroundColor: "#f0f0f0", overflow: "hidden" }}>
                    <Box sx={{ height: "100%", width: `${pct}%`, backgroundColor: r.color,
                      borderRadius: 4, transition: "width 0.4s ease" }} />
                  </Box>
                </Box>
              );
            })}
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="caption" color="text.secondary">
              Total Non-Teaching Staff: <strong>{totalNonTeaching}</strong>
            </Typography>
          </Paper>
        </Grid>

        {/* Volunteers Overview — expanded breakdown */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                <VolunteerIcon sx={{ mr: 1, verticalAlign: "middle", color: "#336599", fontSize: 20 }} />
                Volunteers Overview
              </Typography>
              <Tooltip title="View volunteers"><DrillDownIcon sx={{ fontSize: 16, color: "#244C77", cursor: "pointer" }}
                onClick={() => navigate("volunteers")} /></Tooltip>
            </Box>
            {[
              { label: "Total",       value: volunteerStats.total,           color: "#244C77" },
              { label: "Approved",    value: volunteerStats.approved,        color: "#4caf50" },
              { label: "Pending",     value: volunteerStats.pending,         color: "#ff9800" },
              { label: "Active",      value: volunteerStats.teaching,        color: "#2196f3" },
              { label: "Inactive",    value: volunteerStats.nonTeaching,     color: "#336599" },
              { label: "Waitlisted",  value: volunteerStats.paraProfessional,color: "#00bcd4" },
            ].map((row) => {
              const maxVal = volunteerStats.total || 1;
              const pct = Math.round((row.value / maxVal) * 100);
              return (
                <Box key={row.label} sx={{ mb: 1.2 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.3 }}>
                    <Typography variant="caption" fontWeight={500} color="text.secondary">{row.label}</Typography>
                    <Chip label={row.value} size="small"
                      sx={{ height: 18, fontSize: 10, fontWeight: 700, backgroundColor: row.color + "20", color: row.color }} />
                  </Box>
                  <Box sx={{ height: 7, borderRadius: 4, backgroundColor: "#f0f0f0", overflow: "hidden" }}>
                    <Box sx={{ height: "100%", width: `${pct}%`, backgroundColor: row.color,
                      borderRadius: 4, transition: "width 0.4s ease" }} />
                  </Box>
                </Box>
              );
            })}
          </Paper>
        </Grid>

        {/* Payment Summary */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" mb={2}>
              <PaymentIcon sx={{ mr: 1, verticalAlign: "middle", color: "#244C77" }} />
              Payment Summary
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <Paper sx={{ p: 2, textAlign: "center", borderRadius: 2, backgroundColor: "#e8f5e9" }}>
                  <Typography variant="h5" fontWeight="bold" color="#4caf50">
                    ${paymentSummary.totalPayments?.toLocaleString() || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Total Payments</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Paper sx={{ p: 2, textAlign: "center", borderRadius: 2, backgroundColor: "#fff3e0" }}>
                  <Typography variant="h5" fontWeight="bold" color="#ff9800">
                    ${paymentSummary.pending?.toLocaleString() || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Pending</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Paper sx={{ p: 2, textAlign: "center", borderRadius: 2, backgroundColor: "#fce4ec" }}>
                  <Typography variant="h5" fontWeight="bold" color="#f44336">
                    ${paymentSummary.refunds?.toLocaleString() || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Refunds</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Paper sx={{ p: 2, textAlign: "center", borderRadius: 2, backgroundColor: "#e3f2fd" }}>
                  <Typography variant="h5" fontWeight="bold" color="#2196f3">
                    ${paymentSummary.reimbursements?.toLocaleString() || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Reimbursements</Typography>
                </Paper>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      <Toast open={toast.open} handleClose={() => setToast({ ...toast, open: false })}
        severity={toast.severity} message={toast.message} />
    </Box>
  );
};

export default AdminDashboard;
